import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Package, Loader2, CheckCircle, XCircle, Minimize2, Sparkles } from "lucide-react";
import { useImport } from "@/contexts/ImportContext";

export function ImportProgressDialog() {
  const {
    isDialogOpen,
    currentIndex,
    siteName,
    results,
    successCount,
    errorCount,
    minimize,
    closeDialog,
    phase,
    embedding,
  } = useImport();

  if (!isDialogOpen || results.length === 0) return null;

  const isComplete = phase === "done";
  const isImportingPhase = phase === "importing";
  const isEmbeddingPhase = phase === "embedding";

  const totalImport = results.length;
  const finishedImport = results.filter(r => r.status === "success" || r.status === "error").length;
  const importPercent = totalImport > 0 ? (finishedImport / totalImport) * 100 : 0;

  const totalEmbedding = embedding.total;
  const finishedEmbedding = embedding.done + embedding.failed;
  const embeddingPercent = totalEmbedding > 0 ? (finishedEmbedding / totalEmbedding) * 100 : 0;

  return (
    <Dialog open={isDialogOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {isComplete
              ? `Importação de ${siteName} concluída`
              : `Importando de ${siteName}`}
          </DialogTitle>
          <DialogDescription>
            {isImportingPhase
              ? "Etapa 1 de 2: baixando e salvando os produtos."
              : isEmbeddingPhase
                ? "Etapa 2 de 2: indexando produtos para o agente de IA encontrá-los."
                : `${successCount} produto(s) importado(s)${errorCount > 0 ? `, ${errorCount} erro(s)` : ""}${embedding.failed > 0 ? `, ${embedding.failed} sem indexação` : ""}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Phase 1: Import progress */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 font-medium">
                {finishedImport === totalImport ? (
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                )}
                Importando produtos
              </span>
              <span className="text-muted-foreground tabular-nums">
                {finishedImport} / {totalImport}
              </span>
            </div>
            <Progress value={importPercent} className="h-2" />
          </div>

          {/* Phase 2: Embedding progress */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 font-medium">
                {isComplete && embedding.failed === 0 ? (
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                ) : isEmbeddingPhase ? (
                  <Sparkles className="w-3.5 h-3.5 animate-pulse text-accent-foreground" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 text-muted-foreground" />
                )}
                Gerando embeddings (RAG)
              </span>
              <span className="text-muted-foreground tabular-nums">
                {totalEmbedding > 0 ? `${finishedEmbedding} / ${totalEmbedding}` : "—"}
              </span>
            </div>
            <Progress value={embeddingPercent} className="h-2" />
          </div>

          {isImportingPhase && (
            <p className="text-xs text-center text-muted-foreground">
              Importando {Math.min(currentIndex + 1, totalImport)} de {totalImport} produtos...
            </p>
          )}
        </div>

        <ScrollArea className="h-[320px] pr-4">
          <div className="space-y-2">
            {results.map((result) => (
              <div
                key={result.url}
                className="flex items-center gap-3 p-3 border rounded-lg"
              >
                {result.status === "pending" && (
                  <div className="w-5 h-5 rounded-full border-2 border-muted" />
                )}
                {result.status === "importing" && (
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                )}
                {result.status === "success" && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
                {result.status === "error" && (
                  <XCircle className="w-5 h-5 text-destructive" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{result.productName || "Produto"}</p>
                  {result.error && (
                    <p className="text-xs text-destructive truncate">{result.error}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2 pt-4 border-t">
          {!isComplete ? (
            <Button variant="outline" onClick={minimize}>
              <Minimize2 className="w-4 h-4 mr-2" />
              Minimizar
            </Button>
          ) : (
            <Button onClick={closeDialog}>
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
